import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, BellRing, FolderLock, LayoutGrid, Bell, Settings, Shield,
  Plane, Gauge, Home as HomeIcon, PawPrint, Baby, Landmark, Heart, Users, Handshake, Plus, PiggyBank, BookOpen, LifeBuoy,
} from "lucide-react";
import { useExperience } from "../features/experience";

export const HUB_ICONS = { Plane, Gauge, Home: HomeIcon, PawPrint, Baby, Landmark, Heart, Users, Handshake, PiggyBank };

const CORE = [
  { to: "/app/dashboard", label: "Home", icon: LayoutDashboard, id: "home" },
  { to: "/app/calendar", label: "Calendar", icon: Calendar, id: "calendar" },
  { to: "/app/responsibilities", label: "Reminders", icon: BellRing, id: "reminders" },
  { to: "/app/vault", label: "Vault", icon: FolderLock, id: "vault" },
];

const Row = ({ to, id, icon: Icon, label, onNav, badge }) => (
  <NavLink to={to} data-testid={`nav-${id}`} onClick={onNav}
    className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-[#A7C4A0]/25 text-[#0F766E]" : "text-[#636E72] hover:bg-[#A7C4A0]/10 hover:text-[#0F766E]"}`}>
    <Icon className="h-5 w-5" strokeWidth={1.5} /> {label}
    {badge}
  </NavLink>
);

const Heading = ({ children }) => (
  <div className="mb-1 mt-5 flex items-center gap-2 px-3">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">{children}</span>
    <span className="ml-auto h-px flex-1 bg-[#E2E8F0]" />
  </div>
);

// Four-layer navigation (Adaptive Experience Phase 1). One payload drives desktop and mobile:
// Core surfaces, the user's enabled Life Hubs in their order, Together, then Notifications and
// Settings. Navigation can only grow through hub enablement, never new core rows.
export function AdaptiveSidebar({ user, unread, connectUnread, supportAttention, onNav }) {
  const { experience } = useExperience();
  const navigate = useNavigate();
  const hubsById = {};
  (experience?.layers?.life || []).concat(experience?.layers?.together || []).forEach((h) => { hubsById[h.id] = h; });
  const myHubs = (experience?.hubs?.enabled || []).map((id) => hubsById[id]).filter((h) => h && h.layer === "life");

  return (
    <>
      <nav className="flex flex-col gap-1" data-testid="adaptive-nav-core">
        {CORE.filter((i) => !(user?.is_minor && i.id === "vault")).map((i) => <Row key={i.id} {...i} onNav={onNav} />)}
      </nav>

      <Heading>My Hubs</Heading>
      <nav className="flex flex-col gap-1" data-testid="adaptive-nav-hubs">
        {myHubs.map((h) => {
          const Icon = HUB_ICONS[h.icon] || Plane;
          return <Row key={h.id} to={h.route} id={h.id} icon={Icon} label={h.label} onNav={onNav} />;
        })}
        {experience && myHubs.length === 0 && (
          <button data-testid="nav-hubs-empty" onClick={() => { onNav?.(); navigate("/app/hubs"); }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-[#9aa3a0] hover:bg-[#A7C4A0]/10 hover:text-[#0F766E]">
            <Plus className="h-5 w-5" strokeWidth={1.5} /> Choose your hubs
          </button>
        )}
        <Row to="/app/hubs" id="hub-manager" icon={LayoutGrid} label="Hub Manager" onNav={onNav} />
      </nav>

      <Heading>Together</Heading>
      <nav className="flex flex-col gap-1" data-testid="adaptive-nav-together">
        <Row to="/app/household" id="household" icon={Users} label="Household" onNav={onNav} />
        <Row to="/app/connect" id="connect" icon={Handshake} label="Connect" onNav={onNav}
          badge={connectUnread ? <span data-testid="connect-unread-dot" className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[#2563EB]" /> : null} />
        <Row to="/community" id="community" icon={BookOpen} label="Community" onNav={onNav} />
      </nav>

      <div className="mt-5 h-px bg-[#E2E8F0]" />
      <nav className="mt-2 flex flex-col gap-1">
        <Row to="/app/notifications" id="notifications" icon={Bell} label="Notifications" onNav={onNav}
          badge={unread > 0 ? <span data-testid="notif-unread-badge" className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#EF4444] px-1.5 text-[11px] font-semibold text-white">{unread > 99 ? "99+" : unread}</span> : null} />
        <Row to="/app/support" id="support" icon={LifeBuoy} label="Help & Support" onNav={onNav}
          badge={supportAttention > 0 ? <span data-testid="support-attention-dot" className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[#D97706]" title="A support request needs your attention" /> : null} />
        <Row to="/app/settings" id="settings" icon={Settings} label="Settings" onNav={onNav} />
        {user?.is_admin && (
          <NavLink to="/admin/dashboard" data-testid="nav-admin-console" onClick={onNav}
            className="mt-1 flex items-center gap-3 rounded-lg border border-[#0F766E]/20 bg-[#0F766E]/5 px-3 py-2.5 text-sm font-medium text-[#0F766E] hover:bg-[#0F766E]/10">
            <Shield className="h-5 w-5" strokeWidth={1.5} /> Admin Console
          </NavLink>
        )}
      </nav>
    </>
  );
}
